import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { DraftGrade, DraftKeeper, DraftPick } from '../types/fantasy';
import { useDraftIndex, useDraftResults } from '../hooks/useFantasyData';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EmptyState from '../components/EmptyState';
import './DraftResults.css';
import { getDraftPickRating } from './draftPickRating';

const IDP_POSITIONS = new Set(['IDP', 'DL', 'DE', 'DT', 'NT', 'EDGE', 'LB', 'DB', 'CB', 'S']);
const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DST', 'IDP'];

const normalizePosition = (position: string) => {
  const normalized = position.trim().toUpperCase();
  return IDP_POSITIONS.has(normalized) ? 'IDP' : normalized;
};

const positionSlug = (position: string) => (
  normalizePosition(position).toLowerCase().replace(/[^a-z0-9]+/g, '-')
);

const formatRank = (rank: number) => (
  Number.isInteger(rank) ? rank.toString() : rank.toFixed(1)
);

const keeperRatingTone = (label: string) => {
  if (['Automatic', 'Strong', 'Smash', 'Hit'].includes(label)) return 'positive';
  if (['Risky', 'Bust', 'Miss'].includes(label)) return 'negative';
  if (['Borderline', 'Bad Luck', 'Limited'].includes(label)) return 'warning';
  if (label === 'Upside') return 'context';
  return 'neutral';
};

const gradeTone = (grade: string) => {
  if (grade.startsWith('A')) return 'positive';
  if (grade.startsWith('B')) return 'context';
  if (grade.startsWith('D')) return 'warning';
  if (grade.startsWith('F')) return 'negative';
  return 'neutral';
};

const DraftGradeBadge: React.FC<{ label: string; grade?: DraftGrade }> = ({ label, grade }) => {
  if (!grade) return null;
  return (
    <span
      className={'draft-team-grade tone-' + gradeTone(grade.Grade)}
      title={
        label + ' grade: ' + grade.Grade + ' (' + grade.Score + '/100); '
        + grade.DecisionsEvaluated + ' of ' + grade.TotalDecisions + ' decisions graded. '
        + grade.Explanation
      }
    >
      <small>{label}</small><b>{grade.Grade}</b>
    </span>
  );
};

const DraftResults: React.FC = () => {
  const { year } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const [showKeepers, setShowKeepers] = useState(false);
  const [healthThreshold, setHealthThreshold] = useState(11);
  const [showAssessment, setShowAssessment] = useState(false);
  const { data: draftIndex } = useDraftIndex();
  const { data: draft, isLoading, error } = useDraftResults(year);

  if (isLoading) {
    return (
      <div className="draft-results-page">
        <LoadingSkeleton height="120px" />
        <LoadingSkeleton height="700px" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="draft-results-page">
        <Link className="draft-back-link" to={'/season/' + year}>
          <ArrowLeftOutlined /> Back to {year} season
        </Link>
        <EmptyState
          message="Draft results unavailable"
          description={'No public draft results were found for the ' + year + ' season.'}
        />
      </div>
    );
  }

  const teams = [...draft.Teams].sort((a, b) => a.DraftSlot - b.DraftSlot);
  const rounds = Array.from(new Set(draft.Picks.map(pick => pick.Round))).sort((a, b) => a - b);
  const keepers = draft.Keepers || [];
  const totalKeepers = draft.TotalKeepers ?? keepers.length;
  const picksByRoundAndTeam = new Map<string, DraftPick[]>();
  const keepersByTeam = new Map<string, DraftKeeper[]>();

  draft.Picks.forEach(pick => {
    const key = pick.Round + ':' + pick.TeamId;
    picksByRoundAndTeam.set(key, [...(picksByRoundAndTeam.get(key) || []), pick]);
  });
  keepers.forEach(keeper => {
    keepersByTeam.set(keeper.TeamId, [...(keepersByTeam.get(keeper.TeamId) || []), keeper]);
  });

  const positions = Array.from(new Set(
    [...draft.Picks, ...keepers]
      .map(player => normalizePosition(player.Position))
      .filter(Boolean),
  )).sort((a, b) => {
    const indexA = POSITION_ORDER.indexOf(a);
    const indexB = POSITION_ORDER.indexOf(b);
    return (indexA === -1 ? POSITION_ORDER.length : indexA)
      - (indexB === -1 ? POSITION_ORDER.length : indexB)
      || a.localeCompare(b);
  });
  const availableDrafts = [...(draftIndex || [])].sort((a, b) => b.Year - a.Year);
  const draftDate = draft.Date
    ? new Date(draft.Date + 'T00:00:00').toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="draft-results-page">
      <header className="draft-results-header">
        <div>
          <Link className="draft-back-link" to={'/season/' + draft.Year}>
            <ArrowLeftOutlined /> Back to {draft.Year} season
          </Link>
          <p className="draft-results-eyebrow">League draft archive</p>
          <h1>{draft.Year} Draft Results</h1>
          <div className="draft-results-meta">
            {draftDate && <span><CalendarOutlined /> {draftDate}</span>}
            <span>{draft.Snake ? 'Snake draft' : 'Linear draft'}</span>
            <span>{draft.TotalPicks} draft picks</span>
            {totalKeepers > 0 && <span>{totalKeepers} keepers</span>}
            <span>{draft.TeamCount} teams</span>
          </div>
        </div>

        {availableDrafts.length > 1 && (
          <label className="draft-year-picker">
            <span>Draft year</span>
            <select
              aria-label="Draft year"
              value={draft.Year}
              onChange={event => navigate('/season/' + event.target.value + '/draft')}
            >
              {availableDrafts.map(item => (
                <option value={item.Year} key={item.Year}>{item.Year}</option>
              ))}
            </select>
          </label>
        )}
      </header>

      <div className="draft-board-controls">
        <section className="draft-legend" aria-label="Position colors">
          {positions.map(position => (
            <span className={'draft-legend-item position-' + positionSlug(position)} key={position}>
              {position}
            </span>
          ))}
        </section>

        <div className="draft-display-controls">
          <div className="draft-view-mode" role="group" aria-label="Draft view mode">
            <button
              type="button"
              className={!showAssessment ? 'is-active' : ''}
              aria-pressed={!showAssessment}
              onClick={() => setShowAssessment(false)}
            >
              Results
            </button>
            <button
              type="button"
              className={showAssessment ? 'is-active' : ''}
              aria-pressed={showAssessment}
              onClick={() => setShowAssessment(true)}
            >
              Assessment
            </button>
          </div>
          <label className="draft-health-threshold">
            <span>Health under</span>
            <input
              aria-label="Health games threshold"
              type="number"
              min="1"
              max="18"
              value={healthThreshold}
              onChange={event => setHealthThreshold(
                Math.min(18, Math.max(1, Number(event.target.value) || 1)),
              )}
            />
            <small>games</small>
          </label>
          {totalKeepers > 0 && (
            <label className="draft-keeper-toggle">
              <input
                type="checkbox"
                checked={showKeepers}
                onChange={event => setShowKeepers(event.target.checked)}
              />
              <span>Show keepers</span>
              <small>{totalKeepers}</small>
            </label>
          )}
        </div>
      </div>

      <div className="draft-board-shell">
        <div className="draft-board-scroll">
          <table className={'draft-board' + (showAssessment ? ' is-assessment' : '')}>
            <thead>
              <tr>
                <th className="draft-round-heading" scope="col">Round</th>
                {teams.map(team => (
                  <th scope="col" key={team.TeamId} title={team.TeamName}>
                    <span className="draft-slot">Pick {team.DraftSlot}</span>
                    <Link to={'/franchise/' + team.FranchiseId + '/season/' + draft.Year}>
                      {team.FranchiseName}
                    </Link>
                    <span className="draft-owner">{team.Owner}</span>
                    {showAssessment && (team.DraftDayGrade || team.ResultGrade) && (
                      <span className="draft-team-grades" aria-label="Draft grades">
                        <DraftGradeBadge label="Day" grade={team.DraftDayGrade} />
                        <DraftGradeBadge label="Result" grade={team.ResultGrade} />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showKeepers && keepers.length > 0 && (
                <tr className="draft-keeper-row">
                  <th className="draft-round" scope="row">
                    <span>Keepers</span>
                  </th>
                  {teams.map(team => (
                    <td key={team.TeamId}>
                      {(keepersByTeam.get(team.TeamId) || []).map(keeper => {
                        const preDraftRankings = keeper.PreDraftRankings || [];
                        const seasonEndRanking = keeper.SeasonEndRanking;
                        const gamesPlayed = seasonEndRanking?.GamesPlayed;
                        const showHealthMarker = typeof gamesPlayed === 'number'
                          && gamesPlayed < healthThreshold;
                        const rating = keeper.Rating;
                        return (
                          <article
                            className={'draft-pick draft-keeper position-' + positionSlug(keeper.Position)}
                            aria-label={'Keeper: ' + keeper.PlayerName}
                            key={keeper.TransactionId}
                          >
                            <span className="draft-keeper-label">Keeper</span>
                            <strong title={keeper.PlayerName}>{keeper.PlayerName}</strong>
                            <div className="draft-pick-rankings" aria-label="Keeper rankings">
                              {preDraftRankings.map(ranking => (
                                <span
                                  className="draft-pick-ranking"
                                  key={'keeper-pre-draft-' + ranking.Position}
                                  title={
                                    'Archived pre-draft position rank as of ' + ranking.AsOf
                                    + '; range ' + ranking.BestRank + '-' + ranking.WorstRank
                                  }
                                >
                                  <small>Pre</small>
                                  <b>{formatRank(ranking.Rank)}</b>
                                </span>
                              ))}
                              {seasonEndRanking && (
                                <span
                                  className="draft-pick-ranking"
                                  title={
                                    'Final total-points position rank'
                                    + (typeof keeper.StartsForTeam === 'number'
                                      ? '; ' + keeper.StartsForTeam + ' starts for ' + team.FranchiseName
                                      : '')
                                  }
                                >
                                  <small>Final</small>
                                  <b>
                                    {seasonEndRanking.Rank}
                                    {showHealthMarker && (
                                      <MedicineBoxOutlined
                                        className="draft-health-icon"
                                        title={gamesPlayed + ' games played'}
                                        aria-label={gamesPlayed + ' games played'}
                                      />
                                    )}
                                  </b>
                                </span>
                              )}
                            </div>
                            {showAssessment && rating && (
                              <div className="draft-pick-ratings" aria-label="Keeper ratings">
                                <span
                                  className={'draft-pick-rating tone-' + keeperRatingTone(rating.DecisionLabel)}
                                  title={rating.DecisionExplanation}
                                >
                                  {rating.DecisionLabel}
                                </span>
                                {rating.OutcomeLabel && (
                                  <span
                                    className={'draft-pick-rating tone-' + keeperRatingTone(rating.OutcomeLabel)}
                                    title={rating.OutcomeExplanation}
                                  >
                                    {rating.OutcomeLabel}
                                  </span>
                                )}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </td>
                  ))}
                </tr>
              )}
              {rounds.map(round => (
                <tr key={round}>
                  <th className="draft-round" scope="row">
                    <span>Round {round}</span>
                    <small aria-hidden="true">
                      {draft.Snake && round % 2 === 0 ? '←' : '→'}
                    </small>
                  </th>
                  {teams.map(team => {
                    const picks = (picksByRoundAndTeam.get(round + ':' + team.TeamId) || [])
                      .sort((a, b) => a.OverallPick - b.OverallPick);
                    return (
                      <td key={team.TeamId}>
                        {picks.map(pick => {
                          const position = normalizePosition(pick.Position);
                          const seasonEndRanking = pick.SeasonEndRanking;
                          const preDraftRankings = pick.PreDraftRankings || [];
                          const gamesPlayed = seasonEndRanking?.GamesPlayed;
                          const showHealthMarker = typeof gamesPlayed === 'number'
                            && gamesPlayed < healthThreshold;
                          const matchingPreDraftRanking = preDraftRankings.find(
                            ranking => normalizePosition(ranking.Position) === position,
                          );
                          const crossPosition = (
                            preDraftRankings.length > 0 && !matchingPreDraftRanking
                          ) || Boolean(
                            seasonEndRanking
                            && normalizePosition(seasonEndRanking.Position) !== position,
                          );
                          const pickRating = getDraftPickRating({
                            preRank: matchingPreDraftRanking?.Rank,
                            draftedRank: pick.PositionPick,
                            finalRank: seasonEndRanking?.Rank,
                            finalPpgRank: seasonEndRanking?.PpgRank,
                            gamesPlayed,
                            healthThreshold,
                            crossPosition,
                          });
                          const ratingBadges = [pickRating.draftDay, pickRating.outcome, pickRating.context]
                            .flatMap(rating => rating ? [rating] : []);
                          return (
                            <article
                              className={'draft-pick position-' + positionSlug(position)}
                              aria-label={
                                'Round ' + pick.Round + ', pick ' + pick.PickInRound
                                + ': ' + pick.PlayerName + ', ' + position + pick.PositionPick
                              }
                              key={pick.DraftPickId}
                            >
                              <div className="draft-pick-topline">
                                <span>{pick.Round}.{pick.PickInRound}</span>
                                <span>#{pick.OverallPick}</span>
                              </div>
                              <strong title={pick.PlayerName}>{pick.PlayerName}</strong>
                              <div className="draft-pick-rankings" aria-label="Positional rankings">
                                {preDraftRankings.map(ranking => (
                                  <span
                                    className="draft-pick-ranking"
                                    key={'pre-draft-' + ranking.Position}
                                    title={
                                      'Archived pre-draft position rank as of ' + ranking.AsOf
                                      + '; range ' + ranking.BestRank + '-' + ranking.WorstRank
                                    }
                                  >
                                    <small>Pre</small>
                                    <b>{formatRank(ranking.Rank)}</b>
                                  </span>
                                ))}
                                <span
                                  className="draft-pick-ranking"
                                  title="League positional acquisition rank, including keepers"
                                >
                                  <small>Drafted</small>
                                  <b>{pick.PositionPick > 0 ? pick.PositionPick : '—'}</b>
                                </span>
                                {seasonEndRanking && (
                                  <span
                                    className="draft-pick-ranking"
                                    title={
                                      'Final total-points position rank'
                                      + (seasonEndRanking.PpgRank
                                        ? '; PPG rank ' + seasonEndRanking.PpgRank
                                        : '')
                                      + (typeof pick.StartsForTeam === 'number'
                                        ? '; ' + pick.StartsForTeam + ' starts for ' + team.FranchiseName
                                        : '')
                                    }
                                  >
                                    <small>Final</small>
                                    <b>
                                      {seasonEndRanking.Rank}
                                      {showHealthMarker && (
                                        <MedicineBoxOutlined
                                          className="draft-health-icon"
                                          title={gamesPlayed + ' games played'}
                                          aria-label={gamesPlayed + ' games played'}
                                        />
                                      )}
                                    </b>
                                  </span>
                                )}
                                {!seasonEndRanking && draft.SeasonFinished === false && (
                                  <span
                                    className="draft-pick-ranking is-pending"
                                    title="Available after the season is finished"
                                  >
                                    <small>Final</small>
                                    <b>…</b>
                                  </span>
                                )}
                              </div>
                              {showAssessment && ratingBadges.length > 0 && (
                                <div className="draft-pick-ratings" aria-label="Pick ratings">
                                  {ratingBadges.map(rating => (
                                    <span
                                      className={'draft-pick-rating tone-' + rating.tone}
                                      title={rating.title}
                                      key={rating.label}
                                    >
                                      {rating.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {(pick.DroppedByDraftingTeam || pick.TradedAwayByDraftingTeam) && (
                                <span className="draft-pick-outcomes" aria-label="Roster outcomes">
                                  {pick.DroppedByDraftingTeam && (
                                    <ArrowDownOutlined
                                      title={'Dropped by ' + team.FranchiseName}
                                      aria-label={'Dropped by ' + team.FranchiseName}
                                    />
                                  )}
                                  {pick.TradedAwayByDraftingTeam && (
                                    <SwapOutlined
                                      title={'Traded away by ' + team.FranchiseName}
                                      aria-label={'Traded away by ' + team.FranchiseName}
                                    />
                                  )}
                                </span>
                              )}
                            </article>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="draft-results-note">
        {showAssessment ? (
          <>Keeper labels compare each decision with ranked players available to draft. Day grades use only
          draft-time information and weight early rounds most heavily. Result grades add final rank, PPG,
          games played, age-adjusted outcomes, and starts for the original team; zero-start players carry no
          result-grade weight.</>
        ) : (
          <>Showing draft results only. Switch to Assessment to reveal pick labels and team grades.</>
        )}
        {' '}PRE ranks are the site owner's archived rankings; drafted ranks include keepers. Health markers
        use the threshold above, bottom-right arrows mark drops or trades, and photos remain omitted.
      </p>
    </div>
  );
};

export default DraftResults;